import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { FaChevronDown, FaChevronRight, FaPlus, FaWarehouse, FaEdit } from 'react-icons/fa';
import NoRecordFound from '../../../components/common/no-record-found';

const ROWS_PER_PAGE = 20;

const CmaList = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState({});
  const [total, setTotal] = useState(0);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(handler);
  }, [search]);

  // Fetch paginated data from server
  const fetchData = async (page, searchValue) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page,
        limit: ROWS_PER_PAGE,
        search: searchValue || '',
      });
      const res = await fetch(`${API_BASE_URL}/api/masters/cma-get?${params.toString()}`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const result = await res.json();
      setData(result.data || []);
      setTotal(result.total || 0);
    } catch (err) {
      setError('Failed to load data');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData(page, debouncedSearch);
    // eslint-disable-next-line
  }, [page, debouncedSearch]);

  // Group data by CMA
  const groupedData = data.reduce((acc, row) => {
    if (!acc[row.cha_cma_id]) {
      acc[row.cha_cma_id] = {
        cha_cma_id: row.cha_cma_id,
        cma_name: row.cma_name,
        type: row.cma_type,
        contact_person: row.contact_person,
        phone: row.phone,
        email: row.email,
        address: row.address,
        gst_no: row.gst_no,
        cma_state: row.cma_state,
        state_code: row.state_code,
        account_no: row.account_no,
        ifsc_code: row.ifsc_code,
        bank_name: row.bank_name,
        bank_address: row.bank_address,
        warehouses: []
      };
    }
    if (row.warehouse_id) {
      acc[row.cha_cma_id].warehouses.push({
        warehouse_id: row.warehouse_id,
        warehouse_name: row.warehouse_name,
        type: row.type,
        location: row.location,
        state: row.state,
        state_code: row.state_code
      });
    }
    return acc;
  }, {});
  const cmaList = Object.values(groupedData);

  // Remove client-side filtering and pagination
  // Filtered data based on search (search on CMA fields only)
  // Pagination
  const totalPages = Math.ceil(total / ROWS_PER_PAGE);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handlePrevPage = () => setPage((p) => Math.max(1, p - 1));
  const handleNextPage = () => setPage((p) => Math.min(totalPages, p + 1));

  const handleExpand = (cha_cma_id) => {
    setExpanded(prev => ({ ...prev, [cha_cma_id]: !prev[cha_cma_id] }));
  };

  return (
    <div className="max-w-8xl mx-auto p-2 bg-white rounded border border-gray-100">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-2">
        <h2 className="text-lg font-medium text-gray-800">CMA/CHA & Warehouse </h2>
        <div className="flex gap-1 w-full sm:w-auto justify-end">
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={handleSearchChange}
            className="border border-gray-200 rounded px-2 py-1 focus:outline-none w-full sm:w-48 text-sm"
          />
          <button
            className="flex items-center gap-1 bg-[#10a5e9] text-white px-3 py-1 text-xs rounded hover:bg-[#0e8dc6] border border-[#10a5e9] font-medium"
            onClick={() => navigate('/cma/new')}
          >
            <FaPlus />
            Add New
          </button>
        </div>
      </div>
      {loading ? (
        <div className="text-center text-blue-600 py-6 text-sm">Loading...</div>
      ) : error ? (
        <div className="text-center text-red-600 py-6 text-sm">{error}</div>
      ) : cmaList.length === 0 ? (
        <NoRecordFound message="No CMA records found." />
      ) : (
        <div className="overflow-x-auto rounded border border-gray-50">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-2 py-2"></th>
               
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">CMA Name</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Type</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Contact Person</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Phone</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Email</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">GST No</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">State</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">State Code</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Address</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Account No</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">IFSC Code</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Bank Name</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Bank Address</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {cmaList.map((row, idx) => (
                <React.Fragment key={row.cha_cma_id}>
                  <tr className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-2 py-1 text-center cursor-pointer" onClick={() => handleExpand(row.cha_cma_id)}>
                      {expanded[row.cha_cma_id] ? <FaChevronDown /> : <FaChevronRight />}
                    </td>
                   
                    <td className="px-3 py-1 font-semibold whitespace-nowrap">{row.cma_name}</td>
                    <td className="px-3 py-1 whitespace-nowrap">{row.type}</td>
                    <td className="px-3 py-1 whitespace-nowrap">{row.contact_person}</td>
                    <td className="px-3 py-1 whitespace-nowrap">{row.phone}</td>
                    <td className="px-3 py-1 whitespace-nowrap">{row.email}</td>
                    <td className="px-3 py-1 whitespace-nowrap">{row.gst_no}</td>
                    <td className="px-3 py-1 whitespace-nowrap">{row.cma_state}</td>
                    <td className="px-3 py-1 whitespace-nowrap">{row.state_code}</td>
                    <td className="px-3 py-1 whitespace-nowrap">{row.address}</td>
                    <td className="px-3 py-1 whitespace-nowrap">{row.account_no}</td>
                    <td className="px-3 py-1 whitespace-nowrap">{row.ifsc_code}</td>
                    <td className="px-3 py-1 whitespace-nowrap">{row.bank_name}</td>
                    <td className="px-3 py-1 whitespace-nowrap">{row.bank_address}</td>
                    <td className="px-3 py-1 whitespace-nowrap">
                      <div className="flex gap-1">
                        <button
                          onClick={() => navigate(`/cma/edit/${row.cha_cma_id}`)}
                          className="text-xs px-2 py-1 bg-[#10a5e9] text-white rounded hover:bg-[#0e8ac2] border border-[#10a5e9] flex items-center gap-1"
                          title="Edit CMA"
                        >
                          <FaEdit />
                          Edit
                        </button>
                        <button
                          onClick={() => navigate(`/cma/${row.cha_cma_id}/add-warehouses`)}
                          className="text-xs px-2 py-1 bg-[#10a5e9] text-white rounded hover:bg-[#0e8ac2] border border-[#10a5e9] flex items-center gap-1"
                          title="Add Warehouses"
                        >
                          <FaWarehouse />
                          Add WH
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expanded[row.cha_cma_id] && (
                    <tr>
                      <td></td>
                      <td colSpan={5} className="p-0">
                        <div className="p-2">
                          {row.warehouses.length === 0 ? (
                            <div className="text-gray-400 text-xs">No warehouses.</div>
                          ) : (
                            <table className="min-w-full text-sm border">
                              <thead>
                                <tr className="bg-gray-50">
                                  <th className="px-2 py-1 text-left font-medium whitespace-nowrap">Warehouse ID</th>
                                  <th className="px-2 py-1 text-left font-medium whitespace-nowrap">Warehouse Name</th>
                                  <th className="px-2 py-1 text-left font-medium whitespace-nowrap">Type</th>
                                  <th className="px-2 py-1 text-left font-medium whitespace-nowrap">Location</th>
                                  <th className="px-2 py-1 text-left font-medium whitespace-nowrap">State</th>
                                  <th className="px-2 py-1 text-left font-medium whitespace-nowrap">State Code</th>
                                </tr>
                              </thead>
                              <tbody>
                                {row.warehouses.map((wh) => (
                                  <tr key={wh.warehouse_id}>
                                    <td className="px-2 py-1 whitespace-nowrap">{wh.warehouse_id}</td>
                                    <td className="px-2 py-1 whitespace-nowrap">{wh.warehouse_name}</td>
                                    <td className="px-2 py-1 whitespace-nowrap">{wh.type}</td>
                                    <td className="px-2 py-1 whitespace-nowrap">{wh.location}</td>
                                    <td className="px-2 py-1 whitespace-nowrap">{wh.state}</td>
                                    <td className="px-2 py-1 whitespace-nowrap">{wh.state_code}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* Pagination Controls */}
      {total > ROWS_PER_PAGE && (
        <div className="flex justify-end items-center gap-2 mt-3 text-xs">
          <button
            onClick={handlePrevPage}
            disabled={page === 1}
            className={`px-2 py-1 rounded border transition-colors duration-100 ${page === 1 ? 'bg-gray-100 text-gray-300 border-gray-200' : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'}`}
          >
            Prev
          </button>
          <span className="text-gray-600">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={handleNextPage}
            disabled={page === totalPages}
            className={`px-2 py-1 rounded border transition-colors duration-100 ${page === totalPages ? 'bg-gray-100 text-gray-300 border-gray-200' : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'}`}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default CmaList;